import React, { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { UploadCloud, Play, X, Loader2, Image as ImageIcon, Film, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MAX_IMAGE_MB = 10;
const MAX_VIDEO_MB = 50;
const MAX_SLOTS = 3;

const ALLOWED_IMAGE_MIMES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
const ALLOWED_VIDEO_MIMES = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-m4v', 'video/ogg'];

const isImage = (type) => ALLOWED_IMAGE_MIMES.includes(type);
const isVideo = (type) => ALLOWED_VIDEO_MIMES.includes(type);

const formatSize = (bytes) => {
  if (!bytes) return '0 KB';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Client-side image compression to <=1920px wide, JPEG 82% quality.
 * Preserves aspect ratio. Passes GIFs through untouched (would lose animation).
 * Returns a File.
 */
const compressImage = async (file) => {
  if (file.type === 'image/gif' || !isImage(file.type)) return file;
  try {
    const bitmap = await createImageBitmap(file);
    const maxW = 1920;
    let { width, height } = bitmap;
    if (width > maxW) {
      height = Math.round((height * maxW) / width);
      width = maxW;
    }
    if (typeof OffscreenCanvas !== 'undefined') {
      const canvas = new OffscreenCanvas(width, height);
      const ctx = canvas.getContext('2d');
      ctx.drawImage(bitmap, 0, 0, width, height);
      const blob = await canvas.convertToBlob({ type: 'image/jpeg', quality: 0.82 });
      const newName = file.name.replace(/\.[^.]+$/, '') + '.jpg';
      return new File([blob], newName, { type: 'image/jpeg' });
    }
    // Fallback: HTMLCanvasElement
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d').drawImage(bitmap, 0, 0, width, height);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.82));
    if (!blob) return file;
    const newName = file.name.replace(/\.[^.]+$/, '') + '.jpg';
    return new File([blob], newName, { type: 'image/jpeg' });
  } catch (e) {
    return file;
  }
};

const validateFile = (file) => {
  const type = file.type;
  if (!isImage(type) && !isVideo(type)) {
    return `Tipo no permitido: ${type || 'desconocido'}. Sólo JPG/PNG/WEBP/GIF o MP4/WEBM/MOV/OGV.`;
  }
  const sizeMB = file.size / (1024 * 1024);
  if (isVideo(type) && sizeMB > MAX_VIDEO_MB) {
    return `El video pesa ${sizeMB.toFixed(1)} MB. Máximo permitido: ${MAX_VIDEO_MB} MB.`;
  }
  if (isImage(type) && sizeMB > MAX_IMAGE_MB) {
    return `La imagen pesa ${sizeMB.toFixed(1)} MB. Máximo permitido: ${MAX_IMAGE_MB} MB (se comprime igualmente).`;
  }
  return null;
};

/**
 * MediaUploader
 *
 * Props:
 *  - value: Array<{ url: string, kind: 'image'|'video', size?: number, name?: string }>
 *  - onChange(next)
 *  - max: number  (default 3)
 *
 * The component uploads via multipart to /api/upload and stores the returned URL.
 */
const MediaUploader = ({ value = [], onChange, max = MAX_SLOTS }) => {
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef(null);

  const videoCount = value.filter((m) => m.kind === 'video').length;

  const uploadOne = async (file) => {
    const err = validateFile(file);
    if (err) {
      toast.error(err);
      return null;
    }

    const finalFile = isImage(file.type) ? await compressImage(file) : file;

    // Enforce max 1 video rule client-side after inspection
    if (isVideo(finalFile.type) && videoCount >= 1) {
      toast.error('Sólo se permite 1 video por evento.');
      return null;
    }

    const form = new FormData();
    form.append('file', finalFile);

    try {
      const resp = await axios.post(`${API}/upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      const fullUrl = resp.data.url.startsWith('http')
        ? resp.data.url
        : `${process.env.REACT_APP_BACKEND_URL}${resp.data.url}`;
      return {
        url: fullUrl,
        path: resp.data.path,
        kind: resp.data.kind,
        size: resp.data.size,
        name: finalFile.name,
      };
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error subiendo el archivo.');
      return null;
    }
  };

  const handleFiles = useCallback(
    async (files) => {
      const fileArr = Array.from(files);
      if (!fileArr.length) return;
      const available = max - value.length;
      if (available <= 0) {
        toast.error(`Ya alcanzaste el máximo de ${max} archivos.`);
        return;
      }
      const batch = fileArr.slice(0, available);
      if (fileArr.length > available) {
        toast.info(`Se subirán ${available} archivo(s). El resto se descarta.`);
      }
      setUploading(true);
      const results = [];
      for (const f of batch) {
         
        const r = await uploadOne(f);
        if (r) results.push(r);
      }
      setUploading(false);
      if (results.length) {
        onChange([...value, ...results]);
        toast.success(`${results.length} archivo(s) subido(s).`);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value, onChange, max, videoCount]  
  );

  const removeAt = (idx) => {
    const next = value.filter((_, i) => i !== idx);
    onChange(next);
  };

  const onDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer?.files?.length) {
      await handleFiles(e.dataTransfer.files);
    }
  };

  const remainingSlots = max - value.length;
  const imageCount = value.filter((m) => m.kind === 'image').length;

  return (
    <div className="space-y-3" data-testid="media-uploader">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[#737373]">
          Podés mezclar orientaciones (vertical, horizontal, cuadrado). <strong>Máximo 1 video.</strong>
          <br />
          Imágenes hasta <strong>{MAX_IMAGE_MB} MB</strong> (se comprimen automáticamente) · Videos hasta <strong>{MAX_VIDEO_MB} MB</strong>.
        </p>
        <span className="text-xs text-[#525252] font-medium whitespace-nowrap" data-testid="uploader-counter">
          {value.length}/{max} · {imageCount} img · {videoCount} video
        </span>
      </div>

      {/* Grid of previews + drop zone */}
      <div className="grid grid-cols-3 gap-3">
        {value.map((item, idx) => (
          <div
            key={item.url || idx}
            className="relative aspect-square rounded-lg overflow-hidden border border-[#E5E5E5] bg-[#0A0A0A] group"
            data-testid={`uploader-slot-${idx}`}
          >
            {item.kind === 'video' ? (
              <div className="w-full h-full flex items-center justify-center bg-black relative">
                <video src={item.url} className="w-full h-full object-contain" muted playsInline preload="metadata" />
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                    <Play className="w-5 h-5 text-black translate-x-0.5" fill="currentColor" />
                  </div>
                </div>
              </div>
            ) : (
              <img src={item.url} alt="" className="w-full h-full object-contain bg-[#F5F5F5]" />
            )}
            <span className="absolute top-1 left-1 inline-flex items-center gap-1 text-[10px] bg-black/70 text-white px-1.5 py-0.5 rounded-full uppercase tracking-wider">
              {item.kind === 'video' ? <><Film className="w-3 h-3" /> Video</> : <><ImageIcon className="w-3 h-3" /> Imagen</>}
            </span>
            {item.size && (
              <span className="absolute bottom-1 left-1 text-[10px] bg-black/70 text-white px-1.5 py-0.5 rounded">
                {formatSize(item.size)}
              </span>
            )}
            <button
              type="button"
              onClick={() => removeAt(idx)}
              className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/95 hover:bg-red-500 hover:text-white flex items-center justify-center text-black transition-colors shadow"
              data-testid={`uploader-remove-${idx}`}
              aria-label="Quitar"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}

        {remainingSlots > 0 && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={onDrop}
            disabled={uploading}
            className={`relative aspect-square rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-2 text-xs font-medium transition-colors ${
              isDragging
                ? 'border-primary bg-primary/5 text-primary'
                : 'border-[#E5E5E5] text-[#737373] hover:border-primary hover:text-primary hover:bg-[#FFF7ED]'
            } ${uploading ? 'opacity-60 cursor-wait' : 'cursor-pointer'}`}
            data-testid="uploader-dropzone"
          >
            {uploading ? (
              <>
                <Loader2 className="w-6 h-6 animate-spin" />
                <span>Subiendo...</span>
              </>
            ) : (
              <>
                <UploadCloud className="w-6 h-6" />
                <span className="text-center px-2">
                  Arrastrá o hacé click<br />
                  <span className="text-[10px] opacity-80">JPG · PNG · MP4</span>
                </span>
              </>
            )}
          </button>
        )}

        {/* Fill remaining grid with empty placeholder cells for aesthetic */}
        {Array.from({ length: Math.max(0, remainingSlots - 1) }).map((_, i) => (
          <div
            key={`empty-${i}`}
            className="aspect-square rounded-lg border-2 border-dashed border-[#F5F5F5] flex items-center justify-center opacity-50"
          >
            <UploadCloud className="w-5 h-5 text-[#D4D4D4]" />
          </div>
        ))}
      </div>

      <input
        ref={inputRef}
        type="file"
        multiple
        accept={[...ALLOWED_IMAGE_MIMES, ...ALLOWED_VIDEO_MIMES].join(',')}
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
        className="hidden"
        data-testid="uploader-file-input"
      />

      {videoCount > 1 && (
        <div className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700" data-testid="uploader-video-error">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>Sólo se permite un video por evento. Eliminá alguno.</span>
        </div>
      )}
    </div>
  );
};

export default MediaUploader;
